import { hashQuery } from '../../App';
import { useAllPages, useLoad } from '../../data';
import { InkHero, Loading, MoneyText, Panel, PageHead, Tabs, fmtMoney, formatWhen, downloadCsv } from '../../ui';
import { Booking, Money } from '../../types';

// Earnings (canon 09 supplier Overview, flow S6 "Get paid"). Gross, from the
// supplier's own ledger (DECISION-015) — never a guess. "Earned" is completed
// work (the job happened); "upcoming" is confirmed work still ahead. Broken
// down by hotel, and every euro reconciles against a named booking. The
// statement is a CSV (canon S6 step 4). No commission is shown — commission
// structure is P1 (open) and canon 12 forbids computing it until I1 closes.
interface HotelEarning {
  hotelAccountId: string;
  hotelName?: string;
  jobs: number;
  total: Money;
}
interface Earnings {
  from: string;
  to: string;
  earned: { jobs: number; total: Money; byHotel: HotelEarning[] };
  upcoming: { jobs: number; total: Money };
  // Fees owed for hotel-cancelled confirmed jobs (P10 / DECISION-058) — its own
  // gross line, never folded into "earned".
  cancellationFees: { jobs: number; total: Money };
}

type Period = 'month' | 'year';

function windowFor(period: Period): { from: string; to: string } {
  const d = new Date();
  const y = d.getFullYear();
  if (period === 'month') {
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const last = new Date(y, d.getMonth() + 1, 0).getDate();
    return { from: `${y}-${m}-01`, to: `${y}-${m}-${String(last).padStart(2, '0')}` };
  }
  return { from: `${y}-01-01`, to: `${y}-12-31` };
}

export function Earnings() {
  const period: Period = hashQuery().get('p') === 'month' ? 'month' : 'year';
  const { from, to } = windowFor(period);
  const summary = useLoad<Earnings>(`/earnings?from=${from}&to=${to}`);
  const completed = useAllPages<Booking>(`/bookings?status=completed&from=${from}&to=${to}`);
  // Cancelled jobs that carried a cancellation fee (P10) — gross income too, so
  // the statement reconciles EVERY euro the supplier is owed (canon S6), not only
  // completed work. Its own labelled line, never netted.
  const cancelledWithFee = useAllPages<Booking>(`/bookings?status=cancelled&from=${from}&to=${to}`);
  const e = summary.data;
  const jobs = completed.items.slice().sort((a, b) => Date.parse(b.start) - Date.parse(a.start));
  const feeRows = cancelledWithFee.items.filter((b) => b.cancelFee);

  const exportCsv = () =>
    downloadCsv(
      `earnings-${from}-to-${to}.csv`,
      ['Date', 'Product', 'Hotel', 'Guests', 'Earned', 'Currency'],
      [
        ...jobs.map((b) => [
          b.start.slice(0, 10),
          b.productName ?? '',
          b.hotelName ?? '',
          b.guests.adults + b.guests.children,
          (b.supplierAmount.minor / 100).toFixed(2),
          b.supplierAmount.currency,
        ]),
        ...feeRows.map((b) => [
          b.start.slice(0, 10),
          `${b.productName ?? 'Booking'} — cancellation fee`,
          b.hotelName ?? '',
          '',
          (b.cancelFee!.minor / 100).toFixed(2),
          b.cancelFee!.currency,
        ]),
      ],
    );

  return (
    <div>
      <PageHead title="Earnings" sub="Gross, straight from your ledger — every euro against a named booking.">
        <button className="btn ghost" onClick={exportCsv} disabled={!jobs.length}>
          Download CSV
        </button>
      </PageHead>

      <Tabs
        items={[
          { label: 'This month', href: '#/earnings?p=month', active: period === 'month' },
          { label: 'This year', href: '#/earnings?p=year', active: period === 'year' },
        ]}
      />

      <InkHero
        eyebrow={`Earned · ${period === 'year' ? 'this year' : 'this month'}`}
        figure={e ? fmtMoney(e.earned.total) : '…'}
        explain={
          e
            ? `${e.earned.jobs} job${e.earned.jobs === 1 ? '' : 's'} completed · ${fmtMoney(e.upcoming.total)} confirmed and still to come (${e.upcoming.jobs} job${e.upcoming.jobs === 1 ? '' : 's'})`
            : ' '
        }
      />

      {e && e.cancellationFees.jobs > 0 && (
        <Panel title="Cancellation fees" meta="gross · its own line">
          <p style={{ marginBottom: 6 }}>
            <MoneyText money={e.cancellationFees.total} /> from {e.cancellationFees.jobs} hotel
            cancellation{e.cancellationFees.jobs === 1 ? '' : 's'}.
          </p>
          <p className="hint">
            A hotel cancelled a confirmed job close to the start — this is owed to you, on top of what
            you earned. It is never netted against your earnings.
          </p>
        </Panel>
      )}

      <Panel
        title="By hotel"
        meta={e ? `${e.earned.byHotel.length} hotel${e.earned.byHotel.length === 1 ? '' : 's'}` : ''}
        flush
      >
        {!e ? (
          <div className="card-body">
            {summary.error ? <p className="error-line">{summary.error}</p> : <Loading />}
          </div>
        ) : e.earned.byHotel.length === 0 ? (
          <div className="card-body">
            <div className="empty-neutral">No completed jobs in this period yet.</div>
          </div>
        ) : (
          <table>
            <tbody>
              {e.earned.byHotel.map((h) => (
                <tr key={h.hotelAccountId}>
                  <td>{h.hotelName ?? h.hotelAccountId}</td>
                  <td className="hint">
                    {h.jobs} job{h.jobs === 1 ? '' : 's'}
                  </td>
                  <td className="num">
                    <MoneyText money={h.total} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>

      <Panel title="Completed jobs" meta="reconcile every euro" flush>
        {jobs.length === 0 ? (
          <div className="card-body">
            <div className="empty-neutral">Nothing completed in this period.</div>
          </div>
        ) : (
          <table>
            <tbody>
              {jobs.map((b) => (
                <tr key={b.id}>
                  <td className="mono">{formatWhen(b.start)}</td>
                  <td>
                    {b.productName}
                    <br />
                    <span className="hint">{b.hotelName}</span>
                  </td>
                  <td className="num">
                    <MoneyText money={b.supplierAmount} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>
    </div>
  );
}
